import { useTranslation } from "react-i18next";
import React, { ReactElement, useEffect, useState } from "react";
import {
    successToast,
    warningToast,
    DownloadMenu,
    SHARE_ACTION
} from "@idwal/idwal-react-components";
import { sendGAEvent } from "../services/AnalyticsService";
import { getDownloads } from "../queries/downloadQueries";
import { useParams } from "react-router";
import { DocumentData } from "../types/DocumentData";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
    FULL_REPORT_DOC_NAME,
    REPORT_ZIP_NAME,
    TECHNICAL_REPORT_NAME
} from "../constants/inspectionElementNames";
import {
    GA_BUTTON_EVENT,
    GA_CLICK_SHARE_OPTIONS,
    GA_DOCS_AND_IMAGES_DOWNLOAD,
    GA_PDF_DOWNLOAD,
    GA_TECHNICAL_REPORT_DOWNLOAD
} from "../constants/GaEvents";
import downloadFile from "../queries/clientDownloader";
import { Claims } from "../types/Claims";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { VesselInspectionResponse } from "../types/responses/VesselInspectionResponse";
import { ID_CONDITION_INSPECTION } from "../constants/InspectionTypes";
import { getUserHasAccess } from "../utils/userGroups";
import { DocumentResponse } from "../types/responses/documentResponse";
import IdwalAdobeViewSDKClient from "@idwal/idwal-utilities/lib/IdwalAdobeViewSDKClient/IdwalAdobeViewSDKClient";
import { hasAvailableFile } from "../utils/DocumentHelpers";
import { DownloadItem } from "../types/DownloadItem";
import { sharePublicInspection, sharePublicInspectionWithNameAndOrg } from "../services/InspectionService";
import { AppContextType } from "../types/AppContextType";
import { AppContext } from "../App";
import { LandingData } from "../types/responses/landingData";
import { jwtDecode } from "jwt-decode";

export const DownloadAndShare = (props: {
    data: (VesselInspectionResponse & LandingData) | undefined;
}): ReactElement => {
    const { t } = useTranslation("locale");
    const [downloadOption, setDownloadOption] = useState("");
    const showSharingOptions = Boolean(props.data?.showSharingOptions);
    const { inspectionId } = useParams();
    const [fullReportDoc, setFullReportDoc] = useState<
        DocumentData | undefined
    >(undefined);
    const [reportZip, setReportZip] = useState<DocumentData | undefined>(
        undefined
    );
    const [technicalReport, setTechnicalReport] = useState<
        DocumentData | undefined
    >(undefined);
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);
    const viewSDKClient = new IdwalAdobeViewSDKClient();

    const { data, refetch, isLoading } = getDownloads(inspectionId ?? "", user);

    const {
        releaseReportSharing
    } = useFlags();

    const hasFleetAccess = getUserHasAccess(user);
    const shareOptionDisabled =
        !!props.data?.DisableShareOption &&
        props.data.DisableShareOption.toLowerCase() !== "false";

    const { setRestrictedView } = React.useContext(
        AppContext
    ) as AppContextType;

    const downloadKeyMap = [
        { documentKey: FULL_REPORT_DOC_NAME, file: fullReportDoc },
        { documentKey: REPORT_ZIP_NAME, file: reportZip },
        { documentKey: TECHNICAL_REPORT_NAME, file: technicalReport }
    ];

    const updateDocuments = (data: DocumentResponse) => {
        setFullReportDoc(data.fullReport);
        setTechnicalReport(data.technicalReportElement);
        setReportZip(data.zipElement);
    };

    useEffect(() => {
        if (data) {
            updateDocuments(data);

            if (!data?.fullReportPresignedSuccess && data.message) {
                warningToast(data.message);
            }
        }
    }, [data]);

    const showFullReportOption =
        !data?.restrictedReportView && hasAvailableFile(fullReportDoc);

    setRestrictedView(data?.restrictedReportView as boolean);

    const showTechnicalReportOption =
        !data?.restrictedReportView &&
        hasAvailableFile(technicalReport) &&
        props.data &&
        props.data.InspectionType !== ID_CONDITION_INSPECTION;

    let items: DownloadItem[] = [];
    if (hasAvailableFile(reportZip) && !data?.restrictedReportView) {
        items.push({
            label: "Supporting Documents and Images",
            key: REPORT_ZIP_NAME
        });
    }

    if (showFullReportOption) {
        items.unshift({
            label: "Idwal Report",
            key: FULL_REPORT_DOC_NAME
        });
    }

    if (!shareOptionDisabled) {
        items.unshift({
            label: t("documents.share"),
            key: SHARE_ACTION
        });
    }

    if (showTechnicalReportOption) {
        items.push({ label: "Technical Report", key: TECHNICAL_REPORT_NAME });
    }

    let customerName = "";
    let organisation = "";
    let claims;

    if (user) {
        // @ts-ignore
        const token = user.signInUserSession.idToken.jwtToken;

        claims = jwtDecode<Claims>(token);
        customerName = decodeURIComponent(claims["custom:full_name"]);
        organisation = decodeURIComponent(claims["custom:organisation"]);
    }

    const onAgreeAndDownload = async () => {
        await downloadItem();
    };

    function handleGaEvent(documentKey: string) {
        const actionMap: { [name: string]: string } = {
            [FULL_REPORT_DOC_NAME]: GA_PDF_DOWNLOAD,
            [REPORT_ZIP_NAME]: GA_DOCS_AND_IMAGES_DOWNLOAD,
            [TECHNICAL_REPORT_NAME]: GA_TECHNICAL_REPORT_DOWNLOAD
        };

        const action: string = actionMap[documentKey];

        if (action) {
            sendGAEvent(GA_BUTTON_EVENT, action);
        }
    }

    async function downloadItem() {
        handleGaEvent(downloadOption);
        const documentToDownload = downloadKeyMap.find(
            (e) => e.documentKey === downloadOption
        );

        if (!documentToDownload?.file) {
            const warnText = t("downloads.noDownload") + downloadOption;
            console.warn(warnText);
            warningToast(warnText);
            return;
        }

        try {
            const response = await fetch(documentToDownload.file.value.url);
            if (!response || response.status !== 200) {
                console.error(
                    response.statusText +
                        " - " +
                        documentToDownload.file.value.url
                );
                warningToast(t("downloads.downloadFailed"));
                return;
            }

            successToast(t("report.downloadStarted"));
            downloadFile(
                documentToDownload.file.value.url,
                documentToDownload.file.value.fileName
            );
        } catch (error) {
            console.error(error);
            warningToast(t("downloads.downloadFailed"));
        }
    }

    const termsAndConditions = [
        `${t("report.tsAndCs_1", { customerName, organisation })}`,
        `${t("report.tsAndCs_2")}`,
        `${t("report.tsAndCs_3")}`,
        `${t("report.tsAndCs_4")}`
    ];

    const shareTermsAndConditions = [
        `${t("report.shareTsAndCs_1", { organisation, customerName })}`
    ];

    const shareReport = (shareEmail: string) => {
        sharePublicInspection(inspectionId ?? "", shareEmail, user);
    };

    const shareReportWithNameAndOrg = (name: string, org: string, email: string) => {
        sharePublicInspectionWithNameAndOrg(
            name,
            org, // Custoemr org used to update rights manager
            props.data?.organization as string, // Organization used in sharing email
            email,
            inspectionId ?? "",
            user,
            {
                vessel: props.data?.ShipName as string,
                inspectionNumber: props.data?.InspectionNumber as string,
                inspectionType: props.data?.InspectionType as string
            }
        )
    };

    const previewFile = () => {
        refetch()
            .then((response) => {
                if (response.data?.fullReport) {
                    viewSDKClient.ready().then(() => {
                        viewSDKClient.previewFile(
                            process.env.REACT_APP_ADOBE_API_KEY,
                            response.data?.fullReport.value.url,
                            response.data?.fullReport.value.fileName,
                            response.data?.fullReport.key,
                            {
                                showAnnotationTools: false,
                                showDownloadPDF: false,
                                showPrintPDF: false,
                                embedMode: "LIGHT_BOX"
                            }
                        );
                        viewSDKClient.registerEventsHandler();
                    });
                    updateDocuments(response.data);
                } else {
                    // Error state
                    console.error("Full report not in response");
                }
            })
            .catch(() => console.error("Error fetching document"));
    };

    if (isLoading || (items.length === 0 && !fullReportDoc)) {
        return <></>;
    }

    const openInNewTab = (url: string) => {
        const newWindow = window.open(url, "_blank", "noopener,noreferrer");
        if (newWindow) newWindow.opener = null;
    };

    const redirectToSharingOptions = () => {
        sendGAEvent(GA_BUTTON_EVENT, GA_CLICK_SHARE_OPTIONS);
        openInNewTab(
            `https://${process.env.REACT_APP_PRE_SALE_URL}/manage-sharing/${inspectionId}?sell=true`
        );
    };

    return (
        <DownloadMenu
            downloads={items}
            onAgreeAndDownload={onAgreeAndDownload}
            setDownloadOption={setDownloadOption}
            onPeview={previewFile}
            modalHeader={t("report.reportSharingTsAndCs")}
            termsAndConditions={termsAndConditions}
            termsAndConditionsLink={
                hasFleetAccess
                    ? "https://www.idwalmarine.com/terms-and-conditions/idwal-id"
                    : "https://www.idwalmarine.com/terms-and-conditions/inspection-reports"
            }
            previewMode={hasAvailableFile(fullReportDoc) && fullReportDoc?.value?.fileName.endsWith(".pdf")}
            shareTermsAndConditions={shareTermsAndConditions}
            onShare={shareReport}
            onShareWithNameAndOrg={shareReportWithNameAndOrg}
            showShareOptions={showSharingOptions}
            onClickShareOptions={redirectToSharingOptions}
            releaseReportSharingFlag={releaseReportSharing}
        />
    );
};
